import React, { useEffect, useState } from 'react';
import BarraFiltros from './BarraFiltros';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getHttpFunctionUrl, postJsonWithAuth } from '../firebase';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import L from 'leaflet';

function numeroSeguro(valor) {
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
}

function corrigirTextoLegado(valor) {
  const texto = String(valor || '').trim();
  if (!/[ÃÂ]/.test(texto)) return texto;

  try {
    const bytes = Uint8Array.from(Array.from(texto), (caractere) => caractere.charCodeAt(0));
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes).trim() || texto;
  } catch (_) {
    return texto;
  }
}

function obterNomeCategoria(valor, categoriaId, tags) {
  const tagOficial = tags.find((tag) => String(tag.id) === String(categoriaId));
  return corrigirTextoLegado(tagOficial?.nome_tag || valor) || 'Sem categoria';
}

function HeatmapLayer({ points }) {
  const map = useMap();

  useEffect(() => {
    if (!points.length) {
      return undefined;
    }

    const maiorQuantidade = Math.max(...points.map((point) => point.quantidade), 1);
    const heatData = points.map((point) => [
      point.latitude,
      point.longitude,
      Math.max(0.2, point.quantidade / maiorQuantidade),
    ]);
    const heatLayer = L.heatLayer(heatData, {
      radius: 32,
      blur: 24,
      maxZoom: 16,
      minOpacity: 0.34,
    }).addTo(map);

    return () => map.removeLayer(heatLayer);
  }, [map, points]);

  return null;
}

function classificarCurvaABC(categorias) {
  const total = categorias.reduce((soma, categoria) => soma + categoria.quantidade, 0);
  let acumulado = 0;

  return categorias.map((categoria, index) => {
    acumulado += categoria.quantidade;
    const percentualAcumulado = total > 0 ? acumulado / total : 0;
    let curva = 'C';
    let cor = '#6c757d';

    if (index === 0 || percentualAcumulado <= 0.8) {
      curva = 'A';
      cor = '#dc3545';
    } else if (percentualAcumulado <= 0.95) {
      curva = 'B';
      cor = '#d97706';
    }

    return { ...categoria, curva, cor };
  });
}

function descreverPrioridade(curva) {
  if (curva === 'A') return 'A · Alta';
  if (curva === 'B') return 'B · Média';
  return 'C · Baixa';
}

const AbaRelatorios = ({ usuarioAtual, isAdmin, podeVerRedeCompleta = false, podeReconstruirRadar = false, tags = [] }) => {
  const [lojasOficiais, setLojasOficiais] = useState([]);
  const [localSelecionado, setLocalSelecionado] = useState('todos');
  const [dadosEngajamento, setDadosEngajamento] = useState([]);
  const [resumoEngajamento, setResumoEngajamento] = useState({
    periodo_inicio: null,
    periodo_fim: null,
    total_curtidas: 0,
    produtos_com_curtidas: 0,
    ranking_completo: false,
    categorias: [],
  });
  const [dadosRadar, setDadosRadar] = useState({
    categorias: [],
    celulas: [],
    total_interesses: 0,
    atualizado_em: null,
    precisa_reconstrucao: false,
  });
  const [carregandoLojas, setCarregandoLojas] = useState(true);
  const [carregandoDados, setCarregandoDados] = useState(false);
  const [reconstruindoRadar, setReconstruindoRadar] = useState(false);
  const [erro, setErro] = useState('');
  const montarUrlFuncaoHttp = getHttpFunctionUrl;

  useEffect(() => {
    let ativo = true;

    const carregarLojas = async () => {
      setCarregandoLojas(true);
      setErro('');

      try {
        const resposta = await postJsonWithAuth(montarUrlFuncaoHttp('gerenciarUsuariosPainel'), {
          action: 'list_report_stores',
        });
        const dadosRetorno = await resposta.json().catch(() => ({}));

        if (!resposta.ok || !dadosRetorno?.sucesso) {
          throw new Error(dadosRetorno?.erro || 'Não foi possível carregar as lojas do relatório agora.');
        }

        const lojas = Array.isArray(dadosRetorno?.stores) ? dadosRetorno.stores : [];

        lojas.sort((a, b) => a.nome.localeCompare(b.nome));
        if (!ativo) return;

        setLojasOficiais(lojas);
        if (!podeVerRedeCompleta && lojas.length > 0) {
          setLocalSelecionado((atual) => atual === 'todos' ? usuarioAtual.id : atual);
        }
      } catch (error) {
        console.error('Erro ao carregar lojas do relatório:', error);
        if (ativo) setErro('Não foi possível carregar as lojas para o relatório.');
      } finally {
        if (ativo) setCarregandoLojas(false);
      }
    };

    void carregarLojas();
    return () => { ativo = false; };
  }, [podeVerRedeCompleta, usuarioAtual?.id]);

  const carregarEngajamento = async () => {
    try {
      const resposta = await postJsonWithAuth(montarUrlFuncaoHttp('gerenciarUsuariosPainel'), {
        action: 'get_engagement_report',
        global: localSelecionado === 'todos',
        storeId: localSelecionado === 'todos' ? '' : localSelecionado,
      });
      const dadosRetorno = await resposta.json().catch(() => ({}));

      if (!resposta.ok || !dadosRetorno?.sucesso) {
        throw new Error(dadosRetorno?.erro || 'Não foi possível carregar o resumo de engajamento agora.');
      }

      return dadosRetorno?.report || null;
    } catch (error) {
      console.error('Erro ao carregar resumo de engajamento:', error);
      return null;
    }
  };

  const carregarRadar = async () => {
    const obterRadar = httpsCallable(getFunctions(), 'obterRadarMercado');
    const resposta = await obterRadar(
      localSelecionado === 'todos'
        ? { global: true }
        : { lojaId: localSelecionado },
    );
    return resposta.data || {};
  };

  const carregarDados = async () => {
    if (carregandoLojas || (!podeVerRedeCompleta && localSelecionado === 'todos')) {
      return;
    }

    setCarregandoDados(true);
    setErro('');

    try {
      const [relatorio, radar] = await Promise.all([
        carregarEngajamento(),
        carregarRadar(),
      ]);
      const ranking = Array.isArray(relatorio?.ranking_produtos)
        ? relatorio.ranking_produtos.map((item) => {
          const nomeOriginal = corrigirTextoLegado(item.nome_produto) || 'Produto';
          return {
            ...item,
            nomeOriginal,
            nomeCurto: nomeOriginal.length > 16 ? `${nomeOriginal.substring(0, 14)}...` : nomeOriginal,
            engajamento: numeroSeguro(item.likes),
            local: corrigirTextoLegado(item.loja_nome) || 'Loja',
            categoria_nome: obterNomeCategoria(item.categoria_nome, item.categoria_id, tags),
          };
        })
        : [];
      setDadosEngajamento(ranking);
      setResumoEngajamento({
        periodo_inicio: relatorio?.periodo_inicio || null,
        periodo_fim: relatorio?.periodo_fim || null,
        total_curtidas: numeroSeguro(relatorio?.total_curtidas),
        produtos_com_curtidas: numeroSeguro(relatorio?.produtos_com_curtidas),
        ranking_completo: relatorio?.ranking_completo === true,
        categorias: Array.isArray(relatorio?.categorias) ? relatorio.categorias : [],
      });
      setDadosRadar({
        categorias: Array.isArray(radar.categorias)
          ? radar.categorias.map((categoria) => ({
            ...categoria,
            nome: obterNomeCategoria(categoria.nome, categoria.id, tags),
          }))
          : [],
        celulas: Array.isArray(radar.celulas) ? radar.celulas : [],
        total_interesses: numeroSeguro(radar.total_interesses),
        atualizado_em: radar.atualizado_em || null,
        precisa_reconstrucao: radar.precisa_reconstrucao === true,
      });
    } catch (error) {
      console.error('Erro ao carregar dados do relatório:', error);
      setErro('Não foi possível carregar os dados consolidados do Radar. Tente novamente.');
    } finally {
      setCarregandoDados(false);
    }
  };

  useEffect(() => {
    void carregarDados();
  }, [carregandoLojas, localSelecionado, podeVerRedeCompleta, tags]);

  const reconstruirRadar = async () => {
    setReconstruindoRadar(true);
    setErro('');

    try {
      const reconstruir = httpsCallable(getFunctions(), 'reconstruirRadarMercado');
      await reconstruir();
      await carregarDados();
    } catch (error) {
      console.error('Erro ao reconstruir Radar:', error);
      setErro('Não foi possível consolidar os alertas existentes agora.');
    } finally {
      setReconstruindoRadar(false);
    }
  };

  const dadosAlertasABC = classificarCurvaABC(
    [...dadosRadar.categorias].sort((a, b) => b.quantidade - a.quantidade),
  );
  const maiorInteresse = dadosAlertasABC[0] || null;
  const melhorOferta = dadosEngajamento[0] || null;
  const lojaSelecionada = lojasOficiais.find((loja) => loja.id === localSelecionado);
  const latitudeLoja = numeroSeguro(lojaSelecionada?.latitude);
  const longitudeLoja = numeroSeguro(lojaSelecionada?.longitude);
  const centroMapa = latitudeLoja && longitudeLoja
    ? [latitudeLoja, longitudeLoja]
    : [-27.2185, -49.6436];

  if (carregandoLojas) {
    return <div className="routine-panel" style={{ textAlign: 'center' }}><h2 className="routine-section-title">Carregando relatórios...</h2></div>;
  }

  return (
    <div className="reports-premium">
      <div className="reports-premium__header">
        <div>
          <h2 style={{ color: '#001A3F', margin: 0 }}>Radar de Oportunidades</h2>
        </div>

      </div>
      <BarraFiltros rotulo="Filtros dos relatorios">
        <div className="routine-field">
          <label className="routine-label" htmlFor="relatorios-loja">Loja</label>
          <select
            id="relatorios-loja"
            value={localSelecionado}
            onChange={(event) => setLocalSelecionado(event.target.value)}
            className="routine-select"
          >
            {podeVerRedeCompleta && <option value="todos">Ver rede completa</option>}
            {lojasOficiais.map((loja) => (
              <option key={loja.id} value={loja.id}>{loja.nome}</option>
            ))}
          </select>
        </div>
      </BarraFiltros>

      {erro && (
        <div className="routine-callout routine-callout--danger" style={{ marginBottom: '24px' }}>
          <p className="routine-callout__text">{erro}</p>
        </div>
      )}

      {podeReconstruirRadar && dadosRadar.precisa_reconstrucao && (
        <div className="routine-callout routine-callout--warm" style={{ marginBottom: '24px' }}>
          <h3 className="routine-callout__title">Preparar Radar seguro</h3>
          <p className="routine-callout__text">
            Os alertas existentes ainda precisam ser convertidos uma única vez para o novo resumo anônimo.
          </p>
          {podeReconstruirRadar && (
            <button type="button" onClick={reconstruirRadar} disabled={reconstruindoRadar} className="routine-btn routine-btn--primary">
              {reconstruindoRadar ? 'Consolidando alertas...' : 'Consolidar agora'}
            </button>
          )}
        </div>
      )}

      <div className="reports-premium__insights">
        <div className="reports-premium__insight-card reports-premium__insight-card--demand">
          <span className="reports-premium__insight-label">Maior procura atual</span>
          <strong>{maiorInteresse?.nome || 'Ainda sem dados'}</strong>
          <small>
            {maiorInteresse
              ? `${maiorInteresse.quantidade} interesse(s) ativo(s)`
              : 'Aguardando interesses do público'}
          </small>
        </div>
        <div className="reports-premium__insight-card reports-premium__insight-card--engagement">
          <span className="reports-premium__insight-label">Oferta com melhor resposta</span>
          <strong>{melhorOferta?.nomeOriginal || 'Ainda sem dados'}</strong>
          <small>
            {melhorOferta
              ? `${melhorOferta.engajamento} curtida(s) recebida(s)`
              : 'Aguardando respostas às ofertas'}
          </small>
        </div>
      </div>

      <div className="reports-premium__grid">
        <div className="reports-premium__card reports-premium__card--engagement">
          <h3 style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: '10px', color: '#333', marginBottom: '20px' }}>
            Ofertas que mais chamaram atenção
          </h3>
          <div className="reports-premium__chart">
            {carregandoDados ? (
              <div style={{ textAlign: 'center', color: '#999', marginTop: '50px' }}>Atualizando dados...</div>
            ) : dadosEngajamento.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#999', marginTop: '50px' }}>Ainda não há curtidas registradas.</div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={dadosEngajamento} layout="vertical" margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
                  <XAxis type="number" />
                  <YAxis dataKey="nomeCurto" type="category" width={108} tick={{ fontSize: 11 }} />
                  <RechartsTooltip cursor={{ fill: '#f5f5f5' }} />
                  <Bar dataKey="engajamento" fill="#fd7e14" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="reports-premium__card reports-premium__card--demand">
          <h3 style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: '10px', color: '#333', marginBottom: '20px' }}>
            O que o público procura perto de você
          </h3>
          <div className="reports-premium__chart">
            {carregandoDados ? (
              <div style={{ textAlign: 'center', color: '#999', marginTop: '50px' }}>Atualizando dados...</div>
            ) : dadosAlertasABC.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#999', marginTop: '50px' }}>Nenhum interesse declarado nesta região.</div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={dadosAlertasABC.filter((item) => item.curva !== 'C')} margin={{ top: 10, right: 10, left: -20, bottom: 40 }}>
                  <XAxis dataKey="nome" tick={{ fontSize: 11 }} interval={0} angle={-45} textAnchor="end" />
                  <YAxis tick={{ fontSize: 11 }} />
                  <RechartsTooltip cursor={{ fill: '#f5f5f5' }} />
                  <Bar dataKey="quantidade" radius={[4, 4, 0, 0]}>
                    {dadosAlertasABC.filter((item) => item.curva !== 'C').map((entry) => <Cell key={entry.id} fill={entry.cor} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <p className="reports-premium__summary">
            {dadosRadar.total_interesses} interesse(s) ativo(s).
          </p>
        </div>
      </div>

      <div className="reports-premium__grid reports-premium__grid--details">
        <div className="reports-premium__card">
          <h3 style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: '10px', color: '#333', marginBottom: '20px' }}>Resposta às suas ofertas</h3>
          <div className="reports-premium__table-wrap">
            <table className="reports-premium__table reports-premium__table--engagement">
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Produto</th>
                  <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Categoria</th>
                  <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Loja</th>
                  <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Likes</th>
                </tr>
              </thead>
              <tbody>
                {dadosEngajamento.map((item) => (
                  <tr key={`${item.loja_id}-${item.produto_id}`} style={{ borderBottom: '1px solid #eee' }}>
                    <td data-label="Produto" style={{ padding: '12px' }}>{item.nomeOriginal}</td>
                    <td data-label="Categoria" style={{ padding: '12px', color: '#666', fontSize: '12px' }}>{item.categoria_nome || 'Sem categoria'}</td>
                    <td data-label="Loja" style={{ padding: '12px', color: '#666', fontSize: '12px' }}>{item.local}</td>
                    <td data-label="Likes" className="reports-premium__number" style={{ padding: '12px', fontWeight: 'bold', color: '#fd7e14' }}>{item.engajamento}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="reports-premium__card">
          <h3 style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: '10px', color: '#333', marginBottom: '20px' }}>Categorias que mais engajaram</h3>
          <div className="reports-premium__table-wrap">
            {resumoEngajamento.categorias.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#999', marginTop: '50px' }}>Ainda não há curtidas registradas.</div>
            ) : (
              <table className="reports-premium__table reports-premium__table--categories">
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Categoria</th>
                    <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Curtidas</th>
                  </tr>
                </thead>
                <tbody>
                  {resumoEngajamento.categorias.map((item) => {
                    const categoriaId = item.categoria_id || item.id || 'sem-categoria';
                    const categoriaNome = obterNomeCategoria(item.categoria_nome || item.nome, categoriaId, tags);

                    return (
                      <tr key={categoriaId} style={{ borderBottom: '1px solid #eee' }}>
                        <td data-label="Categoria" style={{ padding: '12px' }}>{categoriaNome}</td>
                        <td data-label="Curtidas" className="reports-premium__number" style={{ padding: '12px', fontWeight: 'bold', color: '#fd7e14' }}>{numeroSeguro(item.likes)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <p className="reports-premium__summary">
            {resumoEngajamento.total_curtidas} curtida(s) recebida(s).
          </p>
        </div>

        <div className="reports-premium__card">
          <h3 style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: '10px', color: '#333', marginBottom: '20px' }}>Prioridade de interesse</h3>
          <div className="reports-premium__table-wrap">
            <table className="reports-premium__table reports-premium__table--abc">
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Categoria</th>
                  <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Interesses</th>
                  <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Prioridade</th>
                </tr>
              </thead>
              <tbody>
                {dadosAlertasABC.map((item) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td data-label="Categoria" style={{ padding: '12px', fontWeight: item.curva === 'A' ? 'bold' : 'normal' }}>{item.nome}</td>
                    <td data-label="Interesses" className="reports-premium__number" style={{ padding: '12px' }}>{item.quantidade}</td>
                    <td data-label="Prioridade" className="reports-premium__class" style={{ padding: '12px' }}>
                      <span style={{
                        backgroundColor: item.curva === 'A' ? '#f8d7da' : (item.curva === 'B' ? '#fff3cd' : '#e2e3e5'),
                        color: item.curva === 'A' ? '#721c24' : (item.curva === 'B' ? '#856404' : '#383d41'),
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontWeight: 'bold',
                        fontSize: '11px',
                      }}>
                        {descreverPrioridade(item.curva)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="reports-premium__card reports-premium__map-card">
        <h3 style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: '10px', color: '#333', marginBottom: '20px' }}>Onde há mais interesse</h3>
        <div className="reports-premium__map">
          {dadosRadar.celulas.length === 0 ? (
            <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa', color: '#666', textAlign: 'center', padding: '24px' }}>
              Ainda não há dados suficientes para exibir o mapa desta região.
            </div>
          ) : (
            <MapContainer center={centroMapa} zoom={13} style={{ height: '100%', width: '100%' }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <HeatmapLayer points={dadosRadar.celulas} />
            </MapContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default AbaRelatorios;
